import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { Avatar, Box, CurrentPlanCard, Icon, Screen, Text } from '~/atomic';
import type { propsIconsNames } from '~/atomic/atoms/Icon/interfaces';
import { useAppDispatch, useAppSelector } from '~/store';
import { logoutUser } from '~/store/auth/login/thunks';
import { selectUserData } from '~/store/user/selectors';
import { formatPhoneNumber, obfuscateCPF } from '~/utils/formatData';
import { formatCentsToBRL } from '~/utils/formatFinance';
import { maskCNPJ } from '~/utils/masks';

const GRADIENT_BG = {
  background: 'linear-gradient(135deg, #1D4733 0%, #3F7D5F 100%)',
};

interface InfoRowProps {
  icon: propsIconsNames;
  label: string;
  value?: string;
  trailing?: ReactNode;
}

function InfoRow({ icon, label, value, trailing }: InfoRowProps) {
  return (
    <div className="flex items-center gap-small py-small">
      <Icon name={icon} size={18} color="#808080" rounded={false} className="shrink-0" />
      <div className="flex-1 min-w-0">
        <Text className="text-caption uppercase tracking-wide color-intense-05">
          {label}
        </Text>
        <Text
          className="mt-quark text-paragraphMD color-complement-01 truncate"
          numberOfLines={1}
        >
          {value || '—'}
        </Text>
      </div>
      {trailing}
    </div>
  );
}

interface SettingRowProps {
  icon: propsIconsNames;
  label: string;
  description?: string;
  onClick?: () => void;
  danger?: boolean;
}

function SettingRow({ icon, label, description, onClick, danger }: SettingRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-small w-full px-medium py-small hover:bg-intense-02 text-left transition-colors"
    >
      <Icon
        name={icon}
        size={20}
        color={danger ? '#E63D3D' : '#1D4733'}
        rounded={false}
        className="shrink-0"
      />
      <div className="flex-1 min-w-0">
        <Text className={danger ? 'text-bodyLG color-warning-05' : 'text-bodyLG color-complement-01'}>
          {label}
        </Text>
        {description ? (
          <Text className="text-captionMD color-intense-06">{description}</Text>
        ) : null}
      </div>
      <Icon name="icon-chevron-right" size={16} color="#B0B0B0" rounded={false} />
    </button>
  );
}

function HeroChip({ icon, label }: { icon: propsIconsNames; label: string }) {
  return (
    <span className="inline-flex items-center gap-quark rounded-full bg-intense-01/15 px-smaller py-quark backdrop-blur-sm">
      <Icon name={icon} size={12} color="#FFFFFF" rounded={false} />
      <Text inline className="text-captionLG color-intense-01">
        {label}
      </Text>
    </span>
  );
}

export default function ProfileView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUserData);

  const goEdit = () => navigate('/app/profile/update');

  const documentLabel = user.userType === 'PF' ? 'CPF' : 'CNPJ';
  const documentValue = user.document
    ? user.userType === 'PF'
      ? obfuscateCPF(user.document)
      : maskCNPJ(user.document)
    : undefined;
  const accountTypeLabel = user.userType === 'PF' ? 'Pessoa física' : 'Pessoa jurídica';

  return (
    <Screen showHeader={false} hasPadding={false} hasTabNavigation bgClassName="bg-intense-02">
      <div className="relative w-full overflow-hidden rounded-b-[28px]" style={GRADIENT_BG}>
        <div className="pointer-events-none absolute -right-12 -top-16 h-[180px] w-[180px] rounded-full bg-intense-01/10" />
        <div className="pointer-events-none absolute -left-12 -bottom-16 h-[150px] w-[150px] rounded-full bg-intense-01/[0.06]" />

        <div className="relative px-small pt-large pb-xxxl">
          <Text className="text-headingH1 color-intense-01" as="h1">
            Perfil
          </Text>

          <div className="mt-medium flex flex-col items-center text-center">
            <button
              type="button"
              onClick={goEdit}
              className="relative rounded-large hover:opacity-95 transition-opacity"
              aria-label="Editar perfil"
            >
              <Avatar
                name={user.fullName}
                seed={user.fullName}
                size="xl"
                className="border-2 border-intense-01/30 shadow-level2"
              />
              <span className="absolute -right-1 -bottom-1 h-[30px] w-[30px] rounded-full bg-intense-01 border-2 border-primary-01/10 flex items-center justify-center shadow-level2">
                <Icon name="icon-pencil" size={14} color="#1D4733" rounded={false} />
              </span>
            </button>

            <Text className="mt-small text-headingH2 color-intense-01 truncate max-w-full" numberOfLines={1}>
              {user.fullName || 'Produtor'}
            </Text>
            <Text className="mt-quark text-bodySM color-intense-04 truncate max-w-full" numberOfLines={1}>
              {user.contact.email}
            </Text>

            <div className="mt-smaller flex flex-wrap items-center justify-center gap-quark">
              <HeroChip
                icon={user.userType === 'PF' ? 'icon-circle-user' : 'icon-enterprise'}
                label={accountTypeLabel}
              />
              <HeroChip
                icon={user.emailVerified ? 'icon-circle-check' : 'icon-circle-alert'}
                label={user.emailVerified ? 'E-mail verificado' : 'Verifique o e-mail'}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="-mt-xl px-small flex flex-col gap-medium pb-large">
        <Box className="rounded-large bg-intense-01 p-medium shadow-level1">
          <div className="flex items-center justify-between gap-smaller pt-large">
            <Text className="text-captionLG uppercase tracking-wider color-intense-05">
              Dados pessoais
            </Text>
            <button
              type="button"
              onClick={goEdit}
              className="inline-flex items-center gap-quark rounded-full bg-support-01 px-small py-quark hover:opacity-90 transition-opacity"
            >
              <Icon name="icon-pencil" size={13} color="#1D4733" rounded={false} />
              <Text inline className="text-captionLG color-primary-01">
                Editar
              </Text>
            </button>
          </div>

          <div className="mt-smaller divide-y divide-intense-03">
            <InfoRow icon="icon-user" label="Nome completo" value={user.fullName} />
            <InfoRow
              icon="icon-phone"
              label="Telefone"
              value={user.contact.phone ? formatPhoneNumber(user.contact.phone) : undefined}
            />
            <InfoRow
              icon="icon-email"
              label="E-mail"
              value={user.contact.email}
              trailing={
                <span
                  className={`inline-flex items-center rounded-full px-smaller py-quark ${
                    user.emailVerified ? 'bg-success-01' : 'bg-alert-01'
                  }`}
                >
                  <Text
                    inline
                    className={`text-captionLG ${
                      user.emailVerified ? 'color-success-06' : 'color-alert-06'
                    }`}
                  >
                    {user.emailVerified ? 'Verificado' : 'Pendente'}
                  </Text>
                </span>
              }
            />
            <InfoRow
              icon="icon-document"
              label={documentLabel}
              value={documentValue}
              trailing={<Icon name="icon-lock" size={13} color="#B0B0B0" rounded={false} />}
            />
          </div>
        </Box>

        <CurrentPlanCard onClickUpgrade={() => navigate('/app/subscribe')} />

        {user.farm?.farmName ? (
          <Box className="rounded-large bg-intense-01 p-medium shadow-level1">
            <Text className="text-captionLG uppercase tracking-wider color-intense-05">
              Fazenda
            </Text>
            <div className="mt-smaller divide-y divide-intense-03">
              <InfoRow icon="icon-home" label="Nome" value={user.farm.farmName} />
              {user.farm.activity ? (
                <InfoRow
                  icon="icon-extract"
                  label="Atividade principal"
                  value={user.farm.activity === 'agriculture' ? 'Agricultura' : 'Pecuária'}
                />
              ) : null}
              {user.farm.hectares ? (
                <InfoRow
                  icon="icon-pin-location"
                  label="Área"
                  value={`${user.farm.hectares} hectares`}
                />
              ) : null}
              {user.farm.livestockHeadCount ? (
                <InfoRow
                  icon="icon-deal"
                  label="Cabeças de gado"
                  value={String(user.farm.livestockHeadCount)}
                />
              ) : null}
              {user.farm.annualRevenue ? (
                <InfoRow
                  icon="icon-circle-money"
                  label="Faturamento anual"
                  value={formatCentsToBRL(user.farm.annualRevenue)}
                />
              ) : null}
            </div>
          </Box>
        ) : null}

        <Box className="rounded-large bg-intense-01 shadow-level1 overflow-hidden py-quark divide-y divide-intense-03">
          <SettingRow
            icon="icon-security"
            label="Alterar senha"
            description="Mantenha sua conta protegida"
            onClick={() => navigate('/app/profile/password')}
          />
          <SettingRow icon="icon-help" label="Ajuda" description="Central de suporte" />
          <SettingRow icon="icon-help" label="WhatsApp" description="Vincular número e configurar alertas" onClick={() => navigate('/app/whatsapp')} />
          <SettingRow
            icon="icon-logout"
            label="Sair da conta"
            onClick={async () => {
              await dispatch(logoutUser());
              navigate('/auth/login', { replace: true });
            }}
            danger
          />
        </Box>
      </div>
    </Screen>
  );
}
